import {
  AffiliateWorkflowBuilderHelper,
  IAffiliateBuilderFormTab,
} from './affiliate-workflow-builder-helper';
import { WorkflowBuilderTab } from './WorkflowBuilderTab';

interface WorkflowBuilderTabsProps {
  workflowLabel: string;
  /** Null while no form is open, which is the one-tab case. */
  formTab: IAffiliateBuilderFormTab | null;
  activeTabValue: string;
  onSelectTab: (tabValue: string) => void;
  onCloseFormTab: () => void;
}

/** The document tabs above the builder: the workflow, always open and never closable, and at most
 * one form opened from a step's "View form". Kakashi's Tabs are underline-and-sliding-indicator
 * shaped, so this strip is built directly rather than overriding every trigger class. */
export const WorkflowBuilderTabs = ({
  workflowLabel,
  formTab,
  activeTabValue,
  onSelectTab,
  onCloseFormTab,
}: WorkflowBuilderTabsProps) => {
  const tabValues = formTab
    ? [AffiliateWorkflowBuilderHelper.WORKFLOW_TAB_ID, formTab.formId]
    : [AffiliateWorkflowBuilderHelper.WORKFLOW_TAB_ID];

  // Handled on the tab itself rather than the strip: the strip is not focusable, so a handler
  // there would only ever fire by bubbling from a tab anyway.
  const onTabKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    const nextTabValue =
      tabValues[
        tabValues.indexOf(activeTabValue) + (event.key === 'ArrowLeft' ? -1 : 1)
      ];
    if (!nextTabValue) return;
    event.preventDefault();
    onSelectTab(nextTabValue);
    // Roving tabindex: the tab just left drops to -1, so focus has to follow the selection.
    document
      .getElementById(AffiliateWorkflowBuilderHelper.getTabId(nextTabValue))
      ?.focus();
  };

  return (
    <div
      role="tablist"
      className="flex shrink-0 items-center gap-xs rounded-xl border-1/2 border-border-primary bg-bg-secondary px-md py-sm"
    >
      <WorkflowBuilderTab
        tabValue={AffiliateWorkflowBuilderHelper.WORKFLOW_TAB_ID}
        label={workflowLabel}
        isActive={
          activeTabValue === AffiliateWorkflowBuilderHelper.WORKFLOW_TAB_ID
        }
        onSelect={onSelectTab}
        onKeyDown={onTabKeyDown}
      />
      {formTab && (
        <WorkflowBuilderTab
          tabValue={formTab.formId}
          label={formTab.label}
          isActive={activeTabValue === formTab.formId}
          onSelect={onSelectTab}
          onKeyDown={onTabKeyDown}
          onClose={onCloseFormTab}
        />
      )}
    </div>
  );
};
